/**
 * @file Components/Sections/FeaturedTours.jsx
 * @description Homepage featured tours carousel section.
 * Fetches featured tours from API and displays in a horizontal scroll.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, MapPin, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const FeaturedTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await api.get('/tours', { params: { featured: 'true', limit: 8 } });
        const result = data?.data?.tours || [];
        setTours(result);
      } catch (err) {
        console.error('Error fetching featured tours:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -380 : 380;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-8" />
          <div className="flex gap-6 overflow-hidden">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="min-w-[340px] h-[280px] bg-gray-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (tours.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Featured Tours</h2>
            <p className="text-gray-500 mt-2">Hand-picked experiences our travelers love</p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <Link
              to="/tours"
              className="ml-3 flex items-center gap-1 text-[#243185] font-semibold text-sm hover:underline"
            >
              View All <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {tours.map((tour, index) => (
            <motion.div
              key={tour._id}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="min-w-[280px] md:min-w-[350px] snap-start flex-shrink-0"
            >
              <Link to={`/tours/${tour._id}`} className="group block">
                <div className="relative h-52 rounded-2xl overflow-hidden shadow-md group-hover:shadow-xl transition-shadow">
                  <img
                    src={tour.thumbnail || tour.images?.[0] || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&q=80'}
                    alt={tour.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 text-xs font-bold text-amber-600">
                    <Star size={12} className="fill-current" /> {tour.rating?.toFixed(1)}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg line-clamp-1">{tour.title}</h3>
                    <div className="flex items-center gap-3 text-white/80 text-xs mt-1">
                      <span className="flex items-center gap-1"><MapPin size={11} /> {tour.location}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> {tour.duration} days</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 px-1">
                  <div>
                    <span className="text-lg font-bold text-[#243185]">₹{tour.price?.toLocaleString()}</span>
                    <span className="text-gray-400 text-xs ml-1">/person</span>
                  </div>
                  <span className="text-xs font-medium text-[#243185] group-hover:underline flex items-center gap-1">
                    Explore <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="md:hidden text-center mt-6">
          <Link
            to="/tours"
            className="inline-flex items-center gap-1 text-[#243185] font-semibold text-sm hover:underline"
          >
            View All Tours <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTours;
